// EmployerHome — the guided employer landing ("Hem"). A control tower: surfaces
// what needs attention (applications to review, hires not onboarding, no active
// postings) AND what to set up first (Link, courses, postings, team, library)
// as prioritized action cards, plus a light snapshot. Router for intent.
//
// The action cards come from a single ORDERED rule array fed to <HomeActionStack>
// — first matching rule = hero. To add/remove/reprioritise a card, edit the
// array. New data-driven states slot in here; notification-driven cards are a
// planned fast-follow (fetch top unread → render as actions).
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, FileText, GraduationCap, Users, Library, Plus, ArrowRight, CalendarClock, Lightbulb } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useEmployer } from '@/contexts/EmployerContext';
import { useLinkLauncher } from '@/contexts/LinkLauncherContext';
import { engagementApi } from '@/lib/api/engagementApi';
import { jobApplicationApi } from '@/lib/api/jobApplicationApi';
import { onboardingApi } from '@/lib/api/onboardingApi';
import { sourcesApi } from '@/lib/api/sourcesApi';
import { employerChatApi } from '@/lib/api/employerChatApi';
import type { Employee } from '@/lib/models/engagement';
import { HomeActionStack, type HomeAction } from '@/components/custom-ui/HomeActionStack';
import { useNotificationActions } from '@/components/custom-ui/useNotificationActions';
import { ExplainerCard } from '@/components/custom-ui/ExplainerCard';
import { IconChip } from '@/components/custom-ui/IconChip';
import { LinkAvatar } from '@/components/icons/LinkAvatar';
import { LoadingState } from '@/components/custom-ui/empty-state';
import EmployerProfileSummary from './EmployerProfileSummary';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';

const EmployerHome: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { employerDetails, activeNeeds, isLoadingNeeds } = useEmployer();
    const { openLink, isOpen: linkOpen } = useLinkLauncher();
    // Unread notifications surface as top-priority cards ("something happened").
    const notifActions = useNotificationActions();
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [pendingApps, setPendingApps] = useState(0);
    const [courseCount, setCourseCount] = useState(0);
    const [sourceCount, setSourceCount] = useState(0);
    const [linkAgentCount, setLinkAgentCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const orgId = employerDetails?.organisation?.id;

    useEffect(() => {
        if (!orgId) return;
        let cancelled = false;
        (async () => {
            const [team, mods, srcs, chats] = await Promise.all([
                engagementApi.listOrganisationEmployees(orgId).catch(() => [] as Employee[]),
                // Same source the Moduler page ("Mina Moduler") counts — org-owned
                // courses only, NOT the broader moduleApi.getModules() set.
                onboardingApi.getOnboardingModules().catch(() => ({ modules: [] as unknown[] })),
                sourcesApi.listSources().catch(() => []),
                // Have they ever chatted with Link? Drives the "Lär känna Link" card.
                employerChatApi.list().catch(() => []),
            ]);
            // Count still-open applications across the employer's active needs.
            const lists = await Promise.all(
                activeNeeds.map((n) => jobApplicationApi.getApplicationsForNeed(n.id, 1, 100).catch(() => ({ applications: [], total: 0 })))
            );
            const pending = lists.flatMap((l) => l.applications ?? []).filter((a) => a.status === 'pending').length;
            if (cancelled) return;
            setEmployees(team);
            setCourseCount(mods.modules?.length ?? 0);
            setSourceCount(srcs.length);
            setLinkAgentCount(chats.length);
            setPendingApps(pending);
            setLoading(false);
        })();
        return () => { cancelled = true; };
        // linkOpen: refetch when the Link overlay closes so the "Lär känna Link"
        // card (and course/need counts) reflect anything done in Link, without a
        // full page reload (the overlay doesn't remount Home).
    }, [orgId, activeNeeds, linkOpen]);

    const firstName = user?.first_name || 'där';
    const notStarted = employees.filter((e) => e.onboarding_status === 'not_started').length;
    const activeCount = activeNeeds.filter((n) => n.status === 'active').length;
    // #281 — postings whose deadline passed with applications still to decide.
    const reviewCount = activeNeeds.filter((n) => n.status === 'expired' && (n.undecided_count ?? 0) > 0).length;
    // Brand-new account: nothing set up yet (no postings, no team, no own
    // courses) → lead with the Link intro + getting-started cards.
    const isEmpty = !loading && activeCount === 0 && employees.length === 0 && courseCount === 0;

    if (isLoadingNeeds && activeNeeds.length === 0) return <LoadingState message="Laddar..." />;

    // ── Action rules (ordered by priority; first match = hero) ──────────
    // Edit / reorder / add freely. `when` gates each card; the stack shows the
    // top `max` and falls back to "Allt är i ordning" when none apply.
    const ruleActions: HomeAction[] = [
        {
            key: 'apps', when: pendingApps > 0, dedupeKey: 'employer-application', icon: FileText, tone: 'blue',
            title: pendingApps === 1 ? '1 ny ansökan väntar' : `${pendingApps} nya ansökningar väntar`,
            description: 'Granska kandidater som sökt dina annonser.',
            ctaLabel: 'Granska', ctaIcon: ArrowRight, onClick: () => navigate('/dashboard/needs'),
        },
        {
            key: 'review', when: reviewCount > 0, icon: CalendarClock, tone: 'amber',
            title: reviewCount === 1 ? '1 annons har gått ut och väntar på granskning' : `${reviewCount} annonser har gått ut och väntar på granskning`,
            description: 'Sista ansökningsdag har passerat — gå igenom ansökningarna och fatta beslut.',
            ctaLabel: 'Granska', ctaIcon: ArrowRight, onClick: () => navigate('/dashboard/needs?filter=expired'),
        },
        {
            key: 'onb', when: notStarted > 0, icon: GraduationCap, tone: 'amber',
            title: `${notStarted} ${notStarted === 1 ? 'nyanställd har' : 'nyanställda har'} inte påbörjat onboarding`,
            description: 'Följ upp så de kommer igång med sina moduler.',
            ctaLabel: 'Till medarbetare', ctaIcon: ArrowRight, onClick: () => navigate('/dashboard/employees'),
        },
        {
            // Persists until the employer has actually chatted with Link at least
            // once — not tied to postings/courses (those aren't "using Link").
            key: 'link', when: !loading && linkAgentCount === 0, iconNode: <LinkAvatar className="w-12 h-12" />, tone: 'brand',
            title: 'Lär känna Link',
            description: 'Din AI-assistent som skapar moduler och jobbannonser åt dig — börja här.',
            ctaLabel: 'Chatta med Link', ctaIcon: ArrowRight, onClick: () => openLink(),
        },
        {
            key: 'firstNeed', when: activeCount === 0, icon: Briefcase, tone: 'brand',
            title: isEmpty ? 'Skapa din första jobbannons' : 'Inga publicerade annonser',
            description: 'Skapa en jobbannons för att börja ta emot ansökningar.',
            ctaLabel: 'Skapa annons', ctaIcon: Plus, onClick: () => navigate('/dashboard/needs'),
        },
        {
            key: 'firstCourse', when: !loading && courseCount === 0, icon: GraduationCap, tone: 'brand',
            title: 'Skapa din första onboarding-modul',
            description: 'Bygg en modul som dina nyanställda går igenom.',
            ctaLabel: 'Skapa modul', ctaIcon: Plus, onClick: () => navigate('/dashboard/courses'),
        },
        {
            key: 'invite', when: !loading && employees.length === 0, icon: Users, tone: 'brand',
            title: 'Bjud in din första medarbetare',
            description: 'Koppla ditt team till organisationen.',
            ctaLabel: 'Bjud in', ctaIcon: Plus, onClick: () => navigate('/dashboard/employees'),
        },
        {
            key: 'library', when: !loading && sourceCount === 0, icon: Library, tone: 'neutral',
            title: 'Fyll ditt bibliotek',
            description: 'Ladda upp dokument och bilder som Link kan använda som källor.',
            ctaLabel: 'Till bibliotek', ctaIcon: ArrowRight, onClick: () => navigate('/dashboard/library'),
        },
    ];
    const allGood: HomeAction = {
        key: 'allgood', when: true, icon: Briefcase, tone: 'green',
        title: 'Allt är i ordning',
        description: 'Inga ansökningar att granska och alla nyanställda är igång.',
        ctaLabel: 'Visa annonser', ctaIcon: ArrowRight, onClick: () => navigate('/dashboard/needs'),
    };

    // Notifications are additive: drop any whose concern a data card already
    // covers (the richer data card wins); prepend the rest as top priority.
    const activeKeys = new Set(ruleActions.filter((a) => a.when && a.dedupeKey).map((a) => a.dedupeKey));
    const notifs = notifActions.filter((g) => !g.dedupeKey || !activeKeys.has(g.dedupeKey));
    const actions: HomeAction[] = [...notifs, ...ruleActions];

    const Stat: React.FC<{ icon: typeof Briefcase; value: number; label: string; onClick: () => void }> = ({ icon, value, label, onClick }) => (
        <button onClick={onClick} className="flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors text-left w-full">
            <IconChip icon={icon} />
            <div>
                <p className="text-xl font-bold text-gray-900 leading-none">{value}</p>
                <p className="text-sm text-gray-500 mt-1">{label}</p>
            </div>
        </button>
    );

    return (
        <div className="space-y-6">
            <ExplainerCard id="employer-home" icon={Lightbulb} tone="amber" title="Så funkar startsidan">
                <p className="text-sm text-gray-600 mt-0.5">
                    Din startsida samlar allt som väntar på din åtgärd — <strong>ansökningar att granska</strong>, annonser som gått ut, och nyanställda som behöver komma igång. Åtgärda korten uppifrån och ner så håller du rekryteringen och onboardingen i rörelse.
                </p>
            </ExplainerCard>

            {/* Greeting spans full width so the columns below start level */}
            <div>
                <h2 className="text-xl font-bold text-gray-900">Hej {firstName}!</h2>
                <p className="text-sm text-gray-500">
                    {isEmpty ? 'Välkommen! Så kommer du igång:' : 'Det här behöver din uppmärksamhet.'}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            {/* Left: actions + snapshot */}
            <div className="lg:col-span-2 space-y-6">
                {loading ? (
                    <div className="space-y-3">
                        <div className="h-24 rounded-2xl bg-gray-100 animate-pulse" />
                        <div className="h-20 rounded-2xl bg-gray-50 animate-pulse" />
                    </div>
                ) : (
                    <HomeActionStack actions={actions} fallback={allGood} />
                )}

                {!loading && (
                    <div className={NEXILINK_CARD_WHITE_ELEVATED}>
                        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-50 p-1">
                            <Stat icon={Briefcase} value={activeCount} label="Publicerade annonser" onClick={() => navigate('/dashboard/needs?filter=active')} />
                            <Stat icon={Users} value={employees.length} label="Medarbetare" onClick={() => navigate('/dashboard/employees')} />
                            <Stat icon={GraduationCap} value={notStarted} label="Ej påbörjad onboarding" onClick={() => navigate('/dashboard/employees')} />
                        </div>
                    </div>
                )}
            </div>

            {/* Right rail: identity / context */}
            <div className="lg:col-span-1">
                <EmployerProfileSummary />
            </div>
            </div>
        </div>
    );
};

export default EmployerHome;
